import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
  CustomerUserService,
  CustomerUserSettingsOutputModel,
} from '@cloudia-shared/apis/generated/user-api';
import { PERMISSION } from '@cloudia-shared/tenant/tenant.config';
import { environment } from '@envs/environment';
import { ModalController } from '@ionic/angular';
import { CanAccessService } from '@tcp/keycloak-guards';
import {
  EMPTY,
  Subject,
  catchError,
  distinctUntilKeyChanged,
  filter,
  map,
  take,
  takeUntil,
} from 'rxjs';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { CookiePolicyPageComponent } from 'src/app/features/feature-cookie-policy/components/cookie-policy-page/cookie-policy-page.component';
import { CookieConsentModalComponent } from 'src/app/features/feature-user-settings/components/cookie-consent-modal/cookie-consent-modal.component';
import { KeycloakService } from 'keycloak-angular';

// eslint-disable-next-line @typescript-eslint/ban-types

const hotjarScript = (hotjarId: number) => {
  const script = document.createElement('script');
  script.setAttribute('async', '');
  document.head.appendChild(script);

  const scriptSource = document.createElement('script');
  scriptSource.innerText =
    '(function(h,o,t,j,a,r){' +
    'h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};' +
    'h._hjSettings={hjid:' +
    hotjarId +
    ',hjsv:6};' +
    "a=o.getElementsByTagName('head')[0];" +
    "r=o.createElement('script');r.async=1;" +
    'r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;' +
    'a.appendChild(r);' +
    "})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');";
  document.head.appendChild(scriptSource);
};

@Injectable({ providedIn: 'root' })
export class UserSettingsInitializer {
  public settings: CustomerUserSettingsOutputModel;
  modal: HTMLIonModalElement;
  cookiesConfigured = new Subject<void>();

  constructor(
    private customerUserService: CustomerUserService,
    private readonly modalController: ModalController,
    private readonly canAccessService: CanAccessService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly tagManagerService: GoogleTagManagerService,
    private readonly kc: KeycloakService
  ) {}

  init() {
    this.customerUserService
      .getUserSettings()
      .pipe(
        take(1),
        catchError((err) => {
          console.error('Error downloading userSettings', err);
          return EMPTY;
        })
      )
      .subscribe((settings) => {
        this.settings = settings;

        // check if cookie popup should apear
        if (!this.settings?.cookiePreferences?.necessaryCookies?.accepted) {
          this.router.events
            .pipe(
              filter((event) => event instanceof NavigationEnd),
              takeUntil(this.cookiesConfigured),
              map(() => this.route),
              map((route) => {
                while (route.firstChild) {
                  route = route.firstChild;
                }
                return route;
              }),
              distinctUntilKeyChanged('component')
            )
            .subscribe((route) => {
              if (
                !this.settings?.cookiePreferences?.necessaryCookies?.accepted &&
                route.component !== CookiePolicyPageComponent &&
                !this.modal
              ) {
                this.openCookieConsentPopup();
              } else if (this.modal !== undefined) {
                this.modalController
                  .getTop()
                  .then(
                    async (modal) => (
                      await modal.dismiss(), (this.modal = undefined)
                    )
                  );
              }
            });
        }

        this.tryToLoadScripts();
      });
  }

  async openCookieConsentPopup() {
    this.modal = await this.modalController.create({
      component: CookieConsentModalComponent,
      cssClass: ['cookie-consent-popup'],
      backdropDismiss: false,
    });

    this.modal.onDidDismiss().then((ev) => {
      if (ev.data) {
        this.cookiesConfigured.next();
      }
    });

    return this.modal.present();
  }

  deleteCookie(name: string): void {
    document.cookie =
      name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
  }

  tryToLoadScripts() {
    this.kc.loadUserProfile().then((value) => {
      // check if GA script should be loaded
      if (
        this.settings?.cookiePreferences?.analyticalCookies?.accepted &&
        environment.useGoogleAnalytics &&
        environment.analyticsTrackingId
      ) {
        this.tagManagerService.addGtmToDom();
        this.router.events.subscribe((event) => {
          if (event instanceof NavigationEnd) {
            const gtmTag = {
              event: 'page',
              page_path: event.urlAfterRedirects,
              user_type: value.email.includes('thecloudpeople.com')
                ? 'tcp'
                : 'customer',
            };
            this.tagManagerService.pushTag(gtmTag);
          }
        });
      }
      // check if HJ script should be loaded
      if (
        this.settings?.cookiePreferences?.analyticalCookies?.accepted &&
        environment.useHotJar
      ) {
        hotjarScript(environment.hotjarId);
      }

      // check if all GA and HJ cookies should be deleted
      if (!this.settings?.cookiePreferences?.analyticalCookies?.accepted) {
        this.deleteAllAnalyticalCookies();
      }
    });
  }

  deleteAllAnalyticalCookies(): void {
    this.deleteCookie('_ga');
    this.deleteCookie(`_ga_${environment.analyticsTrackingId.slice(2)}`);
    this.deleteCookie('_hjAbsoluteSessionInProgress');
    this.deleteCookie(`_hjIncludedInSessionSample_${environment.hotjarId}`);
    this.deleteCookie(`_hjSession_${environment.hotjarId}`);
    this.deleteCookie(`_hjSessionUser_${environment.hotjarId}`);

    document.cookie
      ?.split(';')
      ?.filter((cookie) => cookie.includes('_ga'))
      ?.map((cookieWithTime) =>
        cookieWithTime.substring(0, cookieWithTime.indexOf('='))
      )
      ?.forEach((cookieName) => {
        this.deleteCookie(cookieName);
      });
  }
}
